// AAC export via WebCodecs AudioEncoder, wrapped in an ADTS (.aac) container.
// Chromium-based browsers support this; we feature-detect and fall back.

export function aacSupported(): boolean {
  return typeof (globalThis as any).AudioEncoder !== "undefined";
}

const SR_INDEX: Record<number, number> = {
  96000: 0, 88200: 1, 64000: 2, 48000: 3, 44100: 4, 32000: 5,
  24000: 6, 22050: 7, 16000: 8, 12000: 9, 11025: 10, 8000: 11,
};

function adtsHeader(len: number, sampleRate: number, channels: number): Uint8Array {
  const profile = 1; // AAC-LC (object type 2 -> profile field = 1)
  const srIdx = SR_INDEX[sampleRate] ?? 3;
  const frameLen = len + 7;
  const h = new Uint8Array(7);
  h[0] = 0xff;
  h[1] = 0xf1; // MPEG-4, no CRC
  h[2] = (profile << 6) | (srIdx << 2) | ((channels >> 2) & 1);
  h[3] = ((channels & 3) << 6) | ((frameLen >> 11) & 3);
  h[4] = (frameLen >> 3) & 0xff;
  h[5] = ((frameLen & 7) << 5) | 0x1f;
  h[6] = 0xfc;
  return h;
}

export async function exportAac(buffer: AudioBuffer, bitrate = 192000): Promise<Blob> {
  if (!aacSupported()) throw new Error("AAC encoding not supported in this browser. Use WAV export instead.");
  const AE: any = (globalThis as any).AudioEncoder;
  const channels = Math.min(2, buffer.numberOfChannels);
  const sampleRate = buffer.sampleRate;
  const parts: Uint8Array[] = [];

  const chunks: { data: Uint8Array }[] = [];
  let done!: () => void;
  const finished = new Promise<void>((r) => (done = r));

  const encoder = new AE({
    output: (chunk: any) => {
      const buf = new Uint8Array(chunk.byteLength);
      chunk.copyTo(buf);
      chunks.push({ data: buf });
    },
    error: (e: any) => console.error("AAC encode error", e),
  });

  encoder.configure({
    codec: "mp4a.40.2",
    sampleRate,
    numberOfChannels: channels,
    bitrate,
  });

  // Interleave to f32-planar per WebCodecs AudioData expectations.
  const frameSize = 1024;
  const total = buffer.length;
  const planar = new Float32Array(frameSize * channels);
  const chData: Float32Array[] = [];
  for (let c = 0; c < channels; c++) chData.push(buffer.getChannelData(c));

  let ts = 0;
  for (let i = 0; i < total; i += frameSize) {
    const n = Math.min(frameSize, total - i);
    const data = new Float32Array(n * channels);
    // planar layout: [all ch0][all ch1]
    for (let c = 0; c < channels; c++) {
      for (let j = 0; j < n; j++) data[c * n + j] = chData[c][i + j];
    }
    const AudioData: any = (globalThis as any).AudioData;
    const ad = new AudioData({
      format: "f32-planar",
      sampleRate,
      numberOfChannels: channels,
      numberOfFrames: n,
      timestamp: ts,
      data,
    });
    encoder.encode(ad);
    ad.close?.();
    ts += Math.round((n / sampleRate) * 1e6);
  }

  await encoder.flush();
  encoder.close();
  void planar;
  done();
  await finished;

  for (const c of chunks) {
    parts.push(adtsHeader(c.data.byteLength, sampleRate, channels));
    parts.push(c.data);
  }
  return new Blob(parts as BlobPart[], { type: "audio/aac" });
}
